import { Check, Zap, Target, Shield, Award, AlertTriangle, Star } from "lucide-react";

// Point values matching POINTS_CONFIG in server.py (lines 241-282)
const POINTS_CONFIG = {
  batting: {
    run: 1,
    four_bonus: 1,
    six_bonus: 2,
    thirty_bonus: 25,
    fifty_bonus: 50,
    century_bonus: 100,
    duck_penalty: -5,
  },
  bowling: {
    wicket: 25,
    three_wicket_bonus: 50,
    four_wicket_bonus: 100,
    five_wicket_bonus: 150,
    maiden_over: 8,
    economy_min_overs: 2,
    economy: {
      best: { range: "< 6.0", points: 15 },
      good: { range: "6.0 - 7.5", points: 10 },
      average: { range: "7.5 - 9.0", points: 0 },
      poor: { range: "9.0 - 10.5", points: -5 },
      worst: { range: "> 10.5", points: -10 },
    },
  },
  fielding: {
    catch: 8,
    stumping: 12,
    run_out_direct: 12,
    run_out_indirect: 8,
  },
  multipliers: {
    captain: 2,
    vice_captain: 1.5,
  },
  bonus: {
    starting_xi: 5,
  },
  penalty: {
    late_joiner: -25,
  },
};

const PointRow = ({ label, value, isNegative }) => (
  <div className="flex justify-between items-center py-2 border-b border-slate-700/50 last:border-0">
    <span className="text-slate-300">{label}</span>
    <span className={`font-semibold ${isNegative ? "text-red-400" : "text-emerald-400"}`}>
      {isNegative ? "" : "+"}{value} pts
    </span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="text-emerald-400" size={20} />
      <h2 className="text-lg font-semibold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

export default function PointSystemPage() {
  return (
    <div className="max-w-2xl mx-auto pb-8" data-testid="point-system-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Point System</h1>
        <p className="text-slate-400 text-sm">How fantasy points are calculated</p>
      </div>

      {/* Batting Points */}
      <Section title="Batting Points" icon={Zap}>
        <PointRow label="Run scored" value={POINTS_CONFIG.batting.run} />
        <PointRow label="Four bonus" value={POINTS_CONFIG.batting.four_bonus} />
        <PointRow label="Six bonus" value={POINTS_CONFIG.batting.six_bonus} />
        <PointRow label="30 runs milestone" value={POINTS_CONFIG.batting.thirty_bonus} />
        <PointRow label="Half century (50 runs)" value={POINTS_CONFIG.batting.fifty_bonus} />
        <PointRow label="Century (100 runs)" value={POINTS_CONFIG.batting.century_bonus} />
        <PointRow label="Duck (0 runs, dismissed)" value={POINTS_CONFIG.batting.duck_penalty} isNegative />
      </Section>

      {/* Bowling Points */}
      <Section title="Bowling Points" icon={Target}>
        <PointRow label="Wicket" value={POINTS_CONFIG.bowling.wicket} />
        <PointRow label="3-wicket haul bonus" value={POINTS_CONFIG.bowling.three_wicket_bonus} />
        <PointRow label="4-wicket haul bonus" value={POINTS_CONFIG.bowling.four_wicket_bonus} />
        <PointRow label="5-wicket haul bonus" value={POINTS_CONFIG.bowling.five_wicket_bonus} />
        <PointRow label="Maiden over" value={POINTS_CONFIG.bowling.maiden_over} />
        
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <p className="text-slate-400 text-sm mb-2">Economy Rate (per match):</p>
          {Object.entries(POINTS_CONFIG.bowling.economy).map(([key, data]) => (
            <div key={key} className="flex justify-between items-center py-1">
              <span className="text-slate-400 text-sm">{data.range}</span>
              <span className={`text-sm font-medium ${data.points >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {data.points >= 0 ? "+" : ""}{data.points} pts
              </span>
            </div>
          ))}
          <p className="text-slate-500 text-xs mt-2">Minimum {POINTS_CONFIG.bowling.economy_min_overs} overs bowled to qualify for economy bonus/penalty.</p>
        </div>
      </Section>

      {/* Fielding Points */}
      <Section title="Fielding Points" icon={Shield}>
        <PointRow label="Catch" value={POINTS_CONFIG.fielding.catch} />
        <PointRow label="Stumping" value={POINTS_CONFIG.fielding.stumping} />
        <PointRow label="Run out (direct)" value={POINTS_CONFIG.fielding.run_out_direct} />
        <PointRow label="Run out (indirect)" value={POINTS_CONFIG.fielding.run_out_indirect} />
      </Section>

      {/* Match Participation */}
      <Section title="Match Participation" icon={Star}>
        <PointRow label="Playing in starting XI (per match)" value={POINTS_CONFIG.bonus.starting_xi} />
      </Section>

      {/* Captain & Vice-Captain */}
      <Section title="Captain & Vice-Captain" icon={Award}>
        <div className="space-y-2">
          <div className="flex justify-between items-center py-2 border-b border-slate-700/50">
            <div>
              <span className="text-white font-medium">Captain (C)</span>
              <p className="text-slate-500 text-xs">Your top pick</p>
            </div>
            <span className="text-amber-400 font-bold">{POINTS_CONFIG.multipliers.captain}x all points</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <div>
              <span className="text-white font-medium">Vice-Captain (VC)</span>
              <p className="text-slate-500 text-xs">Your backup pick</p>
            </div>
            <span className="text-amber-400 font-bold">{POINTS_CONFIG.multipliers.vice_captain}x all points</span>
          </div>
        </div>
      </Section>

      {/* Late Joiner Penalty */}
      <Section title="Late Joiner Penalty" icon={AlertTriangle}>
        <div className="space-y-3">
          <PointRow label="Per missed match" value={POINTS_CONFIG.penalty.late_joiner} isNegative />
          <p className="text-slate-400 text-sm">
            If you join after matches have started, you'll receive a penalty for each completed match.
            You can recover these points through strong team performance, predictors, and daily rewards.
          </p>
        </div>
      </Section>

      {/* Tips */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Check className="text-emerald-400" size={18} />
          <h3 className="text-emerald-400 font-semibold">Pro Tips</h3>
        </div>
        <ul className="text-slate-300 text-sm space-y-1">
          <li>- Pick all-rounders who both bat and bowl for maximum point potential</li>
          <li>- Captain your most reliable performer for 2x multiplier</li>
          <li>- Watch for players in good form before each gameweek</li>
          <li>- Collect daily rewards to boost your points</li>
        </ul>
      </div>
    </div>
  );
}
